import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import {
  createUserDefinedExpense,
  type CreateUserDefinedExpenseInput,
  deleteUserDefinedExpense,
  type DeleteUserDefinedExpenseInput,
  getExpenseFormulaVariables,
  type GetExpenseFormulaVariablesInput,
  getPreDefinedExpenseById,
  type GetPreDefinedExpenseByIdInput,
  getPreDefinedExpenses,
  type GetPreDefinedExpensesInput,
  getReceivedUserDefinedExpenses,
  type GetReceivedUserDefinedExpensesInput,
  getSharedUserDefinedExpenses,
  type GetSharedUserDefinedExpensesInput,
  getUserDefinedExpenseById,
  type GetUserDefinedExpenseByIdInput,
  getUserDefinedExpenses,
  type GetUserDefinedExpensesInput,
  updateUserDefinedExpense,
  type UpdateUserDefinedExpenseInput,
} from '../../api/expenses';
import {
  type TypeExpenseSet,
  type TypePredefinedExpense,
  type TypeUserDefinedExpense,
} from '../../common/dataTypes';
import { type DefaultMutationError, type DefaultQueryError } from '../index';

import { useGetRouteParams } from './useUtilsHook';

export const useGetPreDefinedExpenses = (
  input: GetPreDefinedExpensesInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypePredefinedExpense[]>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getPreDefinedExpenses', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypePredefinedExpense[]> =
        await getPreDefinedExpenses(input);

      return data;
    },
    ...opts,
  });

export const useGetPreDefinedExpenseById = (
  input: GetPreDefinedExpenseByIdInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypePredefinedExpense>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getPreDefinedExpenseSetById', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypePredefinedExpense> =
        await getPreDefinedExpenseById(input);

      return data;
    },
    ...opts,
  });

export const useGetUserDefinedExpenses = (
  input: GetUserDefinedExpensesInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeUserDefinedExpense[]>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getUserDefinedExpenses', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeUserDefinedExpense[]> =
        await getUserDefinedExpenses(input);

      return data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...opts,
  });

export const useGetSharedUserDefinedExpenses = (
  input: GetSharedUserDefinedExpensesInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeUserDefinedExpense[]>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getSharedUserDefinedExpenses', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeUserDefinedExpense[]> =
        await getSharedUserDefinedExpenses(input);

      return data;
    },
    ...opts,
  });

export const useGetReceivedUserDefinedExpenses = (
  input: GetReceivedUserDefinedExpensesInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeUserDefinedExpense[]>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getReceivedUserDefinedExpenses', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeUserDefinedExpense[]> =
        await getReceivedUserDefinedExpenses(input);

      return data;
    },
    ...opts,
  });

export const useGetExpenseFormulaVariables = (
  input: GetExpenseFormulaVariablesInput,
  opts?: Partial<UseQueryOptions<Awaited<string[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getExpenseFormulaVariables ', input],
    queryFn: async () => {
      const { data }: AxiosResponse<string[]> =
        await getExpenseFormulaVariables(input);

      return data;
    },
    ...opts,
  });

export const useGetUserDefinedExpenseById = (
  input: GetUserDefinedExpenseByIdInput,
  opts?: Partial<
    UseQueryOptions<Awaited<TypeUserDefinedExpense>, DefaultQueryError>
  >
) =>
  useQuery({
    queryKey: ['getUserDefinedExpenseById', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeUserDefinedExpense> =
        await getUserDefinedExpenseById(input);

      return data;
    },
    ...opts,
  });

export const useUpdateUserDefinedExpense = (
  opts?: Partial<
    UseMutationOptions<Awaited<any>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  const { projectId, orgId, modelId } = useGetRouteParams();

  return useMutation({
    mutationKey: ['updateUserDefinedExpense'],
    mutationFn: async (input: UpdateUserDefinedExpenseInput) => {
      const { data }: AxiosResponse<TypeExpenseSet> =
        await updateUserDefinedExpense(input);

      return data;
    },
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: ['getUserDefinedExpenseById'],
      });
      queryClient.refetchQueries({
        queryKey: ['getUserDefinedExpenses'],
      });
      queryClient.refetchQueries({
        queryKey: [
          'getScenarioListDisplay',
          {
            orgId,
            projectId,
            modelId,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useCreateUserDefinedExpense = (
  opts?: Partial<
    UseMutationOptions<Awaited<any>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createUserDefinedExpense'],
    mutationFn: async (input: CreateUserDefinedExpenseInput) => {
      const { data }: AxiosResponse<TypeExpenseSet> =
        await createUserDefinedExpense(input);

      return data;
    },
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: ['getUserDefinedExpenses'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useDeleteUserDefinedExpense = (
  opts?: Partial<
    UseMutationOptions<Awaited<any>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteUserDefinedExpense'],
    mutationFn: async (input: DeleteUserDefinedExpenseInput) => {
      const { data }: AxiosResponse<TypeExpenseSet> =
        await deleteUserDefinedExpense(input);

      return data;
    },
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: ['getUserDefinedExpenses'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};
